import { useEffect, useState, useRef } from "react";
import { cn } from "@/lib/utils";

interface StickyNavItem {
  label: string;
  href: string; // anchor id e.g. "#services"
}

interface StickyNavProps {
  items: StickyNavItem[];
  offset?: number; // scroll offset for active detection
}

const StickyNav = ({ items, offset = 100 }: StickyNavProps) => {
  const [active, setActive] = useState("");
  const [stuck, setStuck] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const onScroll = () => {
      if (ref.current) {
        setStuck(ref.current.getBoundingClientRect().top <= 0);
      }

      for (let i = items.length - 1; i >= 0; i--) {
        const id = items[i].href.replace("#", "");
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= offset) {
          setActive(items[i].href);
          return;
        }
      }
      setActive("");
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [items, offset]);

  const handleClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <nav
      ref={ref}
      aria-label="Page sections"
      className={cn(
        "sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur-sm transition-shadow duration-300",
        stuck && "shadow-sm"
      )}
    >
      <div className="mx-auto max-w-wide px-5 md:px-12">
        <ul className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-3 -mx-1">
          {items.map((item) => (
            <li key={item.href}>
              <a
                href={item.href}
                onClick={(e) => handleClick(e, item.href)}
                className={cn(
                  "whitespace-nowrap font-body text-xs uppercase tracking-widest px-4 py-2 transition-colors duration-200",
                  active === item.href
                    ? "text-accent border-b-2 border-accent"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

export default StickyNav;
